import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from "@tanstack/react-query";
import type { EstadoFactura, Moneda, PagoCreate } from "@uirtus/shared";
import { api } from "./api";
import type { Cliente } from "./clientes";

export interface FacturaPendiente {
  id: string;
  serie: string | null;
  folio: number | null;
  uuid: string | null;
  total: string;
  saldoInsoluto: string;
  moneda: Moneda;
}

/** Las facturas PPD con saldo del cliente elegido, para armar la aplicación del pago. */
export function useFacturasPendientes(clienteId: string | undefined) {
  return useQuery({
    queryKey: ["facturas", "pendientes", clienteId],
    queryFn: () =>
      api<FacturaPendiente[]>(
        `/facturas?clienteId=${clienteId}&soloPendientesDePago=true`,
      ),
    enabled: !!clienteId,
  });
}

export interface DocumentoPago {
  facturaId: string;
  numParcialidad: number;
  impSaldoAnt: string;
  impPagado: string;
  impSaldoInsoluto: string;
  objetoImpDR: string;
}

/** El estado real del pago es el de la factura tipo "P" que lo ampara. */
export interface FacturaPagoBreve {
  id: string;
  estado: EstadoFactura;
  uuid: string | null;
  serie: string | null;
  folio: number | null;
}

/** La ficha del pago sí trae la factura-de-pago completa (`with` sin columnas). */
export interface FacturaPagoResumen extends FacturaPagoBreve {
  fechaTimbrado: string | null;
  xmlPath: string | null;
}

export interface Pago {
  id: string;
  clienteId: string;
  fechaPago: string;
  formaPago: string;
  moneda: Moneda;
  tipoCambio: string | null;
  monto: string;
  numOperacion: string | null;
  rfcEmisorCtaOrd: string | null;
  ctaOrdenante: string | null;
  rfcEmisorCtaBen: string | null;
  ctaBeneficiario: string | null;
  facturaPagoId: string | null;
  creadoAt: string;
}

export interface PagoLista extends Pago {
  cliente: { id: string; rfc: string; razonSocial: string };
  facturaPago: FacturaPagoBreve | null;
  documentos: Pick<DocumentoPago, "facturaId" | "numParcialidad" | "impPagado" | "impSaldoInsoluto">[];
}

export interface DocumentoPagoConFactura extends DocumentoPago {
  factura: FacturaPendiente;
}

export interface PagoConDetalle extends Pago {
  cliente: Cliente;
  facturaPago: FacturaPagoResumen | null;
  documentos: DocumentoPagoConFactura[];
}

const CLAVE = ["pagos"] as const;

export function usePagos(filtros: { clienteId?: string; facturaId?: string } = {}) {
  const parametros = new URLSearchParams();
  if (filtros.clienteId) parametros.set("clienteId", filtros.clienteId);
  if (filtros.facturaId) parametros.set("facturaId", filtros.facturaId);
  const cadena = parametros.toString();

  return useQuery({
    queryKey: [...CLAVE, filtros],
    queryFn: () => api<PagoLista[]>(`/pagos${cadena ? `?${cadena}` : ""}`),
  });
}

export function usePago(id: string) {
  return useQuery({
    queryKey: [...CLAVE, id],
    queryFn: () => api<PagoConDetalle>(`/pagos/${id}`),
    enabled: !!id,
    // Igual que en facturas: mientras el timbrado esté pendiente, la
    // reconciliación lo va a resolver sola y la pantalla debe reflejarlo
    // sin que el usuario recargue ni vuelva a picarle a Timbrar.
    refetchInterval: (query) =>
      query.state.data?.facturaPago?.estado === "PENDIENTE_TIMBRE" ? 15_000 : false,
  });
}

function useMutacionPago<TDatos, TVars>(
  fn: (vars: TVars) => Promise<TDatos>,
  opciones?: UseMutationOptions<TDatos, Error, TVars>,
) {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: fn,
    ...opciones,
    onSuccess: (...args) => {
      void cliente.invalidateQueries({ queryKey: CLAVE });
      void cliente.invalidateQueries({ queryKey: ["facturas"] });
      opciones?.onSuccess?.(...args);
    },
  });
}

export const useCrearPago = () =>
  useMutacionPago((body: PagoCreate) =>
    api<Pago & { facturaPago: FacturaPagoResumen }>("/pagos", {
      method: "POST",
      body,
    }),
  );

export const useEliminarPago = (id: string) =>
  useMutacionPago(() => api<void>(`/pagos/${id}`, { method: "DELETE" }));
